import { useState, useRef, useEffect, useId } from 'react';
import { Box, HStack, VStack, Text, IconButton, Input, Button, Image } from '@chakra-ui/react';
import { X, Pencil, Check, Camera } from 'lucide-react';
import RatingEmoji from '@/components/feedback/RatingEmoji';
import { RATING_EMOJIS, getRatingGlowColor } from '@/utils/restaurantUtils';
import { isDiscardable, type RatingMenuItem } from '../hooks/useRestaurantRating';
import { FOCUS_RING, MAX_PENDING_IMAGES } from '../ratingCategories';
import { useCloudinaryUpload } from '@/hooks/useCloudinaryUpload';
import { useDiscardUploads } from '@/api/mutations/useRestaurantMutations';
import { haptics } from '@/utils/haptics';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

interface MenuItemCardProps {
  /** The menu item data */
  item: RatingMenuItem;
  /** All menu item names for duplicate validation */
  existingNames: string[];
  /** Callback when item is removed */
  onRemove: () => void;
  /** Callback when name is changed */
  onNameChange: (name: string) => void;
  /** Callback when rating is selected */
  onRatingChange: (rating: number) => void;
  /**
   * Callback to report this menu item.
   * Only called when item.menuItemId is defined (existing DB items).
   * Receives the integer DB PK.
   * Note: menuItemName was removed — ReportMenuItemModal uses restaurantName
   * for context; the item name was never wired through to the modal.
   */
  onReport: (menuItemId: number) => void;
  /**
   * Called with the Cloudinary result of a successful photo pick — creates a
   * pending image entry (status: 'pending') on the rating hook's state. The
   * card never calls the rating hook's functions directly; this is bound at
   * MenuItemsStep the same way onReport is.
   */
  onImageUpload: (publicId: string, previewUrl: string) => void;
  /** Mutates the rating hook's state to drop a staged image. Discarding the upload itself (for 'pending'/'failed' images) is a separate side effect the card fires locally in handleImageRemove, not something this prop does. */
  onImageRemove: (publicId: string) => void;
  /** Current member's id, or null until it has resolved. Gates the Camera control. */
  ownerId: number | null;
}

/**
 * Individual menu item card with rating and photo upload
 */
export function MenuItemCard({
  item,
  existingNames,
  onRemove,
  onNameChange,
  onRatingChange,
  onReport,
  onImageUpload,
  onImageRemove,
  ownerId,
}: MenuItemCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(item.name);
  const [duplicateError, setDuplicateError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Ref for the save IconButton — used in the input's onBlur to detect when
  // focus is moving to the save button, preventing the double-fire where
  // onBlur runs handleSaveEdit and then onClick fires it a second time.
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  const { discardUploads } = useDiscardUploads();

  const { openFilePicker, pendingFile, confirmUpload, isUploading } = useCloudinaryUpload({
    mode: 'direct',
    ...(ownerId !== null ? { ownerId } : {}),
    onSuccess: (result) => {
      onImageUpload(result.publicId, result.url);
    },
  });

  // Direct mode's two-step flow only stages the file selection — auto-confirm
  // immediately since this card provides no separate crop/confirm step.
  useEffect(() => {
    if (pendingFile) {
      void confirmUpload();
    }
  }, [pendingFile, confirmUpload]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Check for duplicate names (case-insensitive, excluding current item).
  // TODO: consolidate with MenuItemsStep.checkDuplicate
  const checkDuplicate = (name: string): boolean => {
    const normalizedName = name.trim().toLowerCase();
    return existingNames.some(
      (existing) => existing.toLowerCase() === normalizedName && existing.toLowerCase() !== item.name.toLowerCase()
    );
  };

  const handleStartEdit = () => {
    setEditedName(item.name);
    setIsEditing(true);
    setDuplicateError(false);
    haptics.selection();
    analytics.capture(AnalyticsEvents.MENU_ITEM_EDIT_CLICKED, {
      menu_item_name: item.name,
    });
  };

  const handleSaveEdit = () => {
    const trimmedName = editedName.trim();

    if (!trimmedName) {
      // Revert to original if empty
      setEditedName(item.name);
      setIsEditing(false);
      setDuplicateError(false);
      return;
    }

    if (checkDuplicate(trimmedName)) {
      setDuplicateError(true);
      haptics.error();
      return;
    }

    onNameChange(trimmedName);
    setIsEditing(false);
    setDuplicateError(false);
    haptics.selection();
  };

  const handleCancelEdit = () => {
    setEditedName(item.name);
    setIsEditing(false);
    setDuplicateError(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditedName(newValue);
    // Clear error when typing
    if (duplicateError) {
      setDuplicateError(checkDuplicate(newValue));
    }
  };

  const handleRatingSelect = (value: number) => {
    haptics.selection();
    analytics.capture(AnalyticsEvents.MENU_ITEM_RATE_CLICKED, {
      menu_item_name: item.name,
      rating: value,
    });
    onRatingChange(value);
  };

  const handleImageRemove = (publicId: string, status: RatingMenuItem['pendingImages'][number]['status']) => {
    // No-op while the image is mid-upload: it's neither safely removable
    // from local state nor eligible for a discard yet.
    if (status === 'uploading') return;
    onImageRemove(publicId);
    // Never discard an image that's already linked to the server — only a
    // still-local pick (pending) or one that failed to link is safe to
    // tell the server to discard.
    if (isDiscardable(status)) {
      discardUploads([publicId]);
    }
  };

  const atCapacity = item.pendingImages.length >= MAX_PENDING_IMAGES;
  const isRatingGate = item.rating === 0;
  const isOwnerGate = !isRatingGate && ownerId === null;
  const cameraDisabled = isRatingGate || isOwnerGate;
  const hintText = isRatingGate
    ? 'Rate this dish to add photos'
    : isOwnerGate
      ? "Photos aren't ready yet"
      : null;
  const hintId = useId();

  return (
    <Box>
      <Box
        bg="whiteAlpha.200"
        borderRadius="var(--r-card-compact)"
        p={4}
        borderWidth="1px"
        borderColor="whiteAlpha.300"
        w="100%"
      >
        {/* Header: Name and remove button */}
        <HStack justify="space-between" mb={3}>
          <VStack align="start" spacing={0} flex={1}>
            {isEditing ? (
              <HStack spacing={2} w="100%">
                <Input
                  ref={inputRef}
                  value={editedName}
                  onChange={handleNameInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={(e) => {
                    // If focus is moving to the save button, skip the blur-save.
                    // Without this guard, onBlur fires handleSaveEdit first and
                    // then the button's onClick fires it a second time.
                    if (e.relatedTarget === saveButtonRef.current) return;
                    handleSaveEdit();
                  }}
                  size="sm"
                  bg="whiteAlpha.200"
                  border="1px solid"
                  borderColor={duplicateError ? 'red.500' : 'purple.500'}
                  color="white"
                  _focus={{
                    borderColor: duplicateError ? 'red.500' : 'purple.400',
                    boxShadow: duplicateError ? '0 0 0 1px var(--chakra-colors-red-500)' : 'outline',
                  }}
                  _placeholder={{ color: 'gray.500' }}
                />
                <IconButton
                  ref={saveButtonRef}
                  aria-label="Save name"
                  icon={<Check size={14} />}
                  size="sm"
                  colorScheme={duplicateError ? 'red' : 'purple'}
                  onClick={handleSaveEdit}
                  isDisabled={duplicateError || !editedName.trim()}
                />
              </HStack>
            ) : (
              <HStack spacing={2}>
                <Text color="white" fontWeight="semibold" fontSize="md">
                  {item.name}
                </Text>
                {item.isNew && (
                  <IconButton
                    aria-label="Edit name"
                    icon={<Pencil size={12} />}
                    size="xs"
                    variant="ghost"
                    color="gray.500"
                    minW="20px"
                    h="20px"
                    _hover={{ color: 'purple.400', bg: 'whiteAlpha.100' }}
                    onClick={handleStartEdit}
                  />
                )}
              </HStack>
            )}
            {duplicateError && isEditing && (
              <Text color="red.400" fontSize="xs" mt={1}>
                A dish with this name already exists
              </Text>
            )}
          </VStack>

          {item.isNew && !isEditing && (
            <IconButton
              aria-label="Remove item"
              icon={<X size={16} />}
              size="sm"
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'red.400', bg: 'whiteAlpha.100' }}
              onClick={onRemove}
            />
          )}
        </HStack>

        {/*
         * Emoji rating row.
         *
         * Semantics: role="radiogroup" on the container + role="radio" with
         * aria-checked on each button makes screen readers announce this as a
         * radio group, which matches the single-select behaviour.
         *
         * We use <Box as="button" role="radio"> rather than a native <input
         * type="radio"> because the native button element receives iOS/WKWebView
         * touch events correctly without additional -webkit-tap-highlight-color
         * scaffolding. Capacitor-based apps on iOS can silently drop touch events
         * on non-button elements; retaining the native button boundary prevents
         * that class of regression.
         */}
        <HStack
          spacing={3}
          justify="center"
          role="radiogroup"
          aria-label={`Rate ${item.name}`}
        >
          {RATING_EMOJIS.map((emoji) => {
            const isSelected = item.rating === emoji.value;
            const glowColor = getRatingGlowColor(emoji.value);

            return (
              <Box
                key={emoji.value}
                as="button"
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => handleRatingSelect(emoji.value)}
                display="flex"
                alignItems="center"
                justifyContent="center"
                minW="44px"
                minH="44px"
                transition="all 0.15s ease-out"
                opacity={isSelected ? 1 : 0.7}
                filter={isSelected ? `drop-shadow(0 0 8px ${glowColor})` : 'none'}
                transform={isSelected ? 'scale(1.15)' : 'scale(1)'}
                _hover={{
                  opacity: 1,
                  transform: 'scale(1.1)',
                }}
                _active={{
                  transform: 'scale(0.95)',
                }}
                _focusVisible={FOCUS_RING}
              >
                <RatingEmoji emoji={emoji} size="xl" showBackground />
              </Box>
            );
          })}
        </HStack>

        {/* Photo upload row — thumbnails of staged images, plus the Camera trigger */}
        <HStack spacing={2} mt={3} flexWrap="wrap">
          {item.pendingImages.map((img) => (
            <Box
              key={img.publicId}
              position="relative"
              boxSize="56px"
              borderRadius="var(--r-chip)"
              overflow="hidden"
              flexShrink={0}
            >
              <Image
                src={img.previewUrl}
                alt=""
                boxSize="56px"
                objectFit="cover"
                opacity={img.status === 'uploading' ? 0.5 : 1}
              />
              <IconButton
                aria-label="Remove photo"
                icon={<X size={12} />}
                size="xs"
                position="absolute"
                top="2px"
                right="2px"
                minW="24px"
                h="24px"
                borderRadius="full"
                bg="blackAlpha.700"
                color="white"
                _hover={{ bg: 'red.500' }}
                onClick={() => handleImageRemove(img.publicId, img.status)}
              />
            </Box>
          ))}

          {!atCapacity && (
            <VStack spacing={1} align="start" flexShrink={0}>
              <IconButton
                aria-label="Add photo"
                icon={<Camera size={18} />}
                boxSize="56px"
                variant="outline"
                borderStyle="dashed"
                borderColor="whiteAlpha.400"
                color="gray.400"
                isDisabled={cameraDisabled}
                isLoading={isUploading}
                aria-describedby={hintText ? hintId : undefined}
                onClick={openFilePicker}
                _hover={{ color: 'white', borderColor: 'purple.400' }}
              />
              {hintText && (
                <Text id={hintId} fontSize="xs" color="gray.500" maxW="80px">
                  {hintText}
                </Text>
              )}
            </VStack>
          )}
        </HStack>

        {/* Report button — only shown for items that have a server-side DB PK */}
        {item.menuItemId !== undefined && (
          <HStack justify="flex-end" mt={2}>
            <Button
              variant="link"
              size="sm"
              color="gray.500"
              _hover={{ color: 'orange.400' }}
              aria-label={`Report ${item.name}`}
              // Non-null assertion is safe: this button only renders inside
              // `item.menuItemId !== undefined` guard (see outer conditional).
              onClick={() => onReport(item.menuItemId!)}
            >
              Report
            </Button>
          </HStack>
        )}
      </Box>
    </Box>
  );
}

export default MenuItemCard;
